import React, { useState, useEffect } from 'react';
import { PageHeader } from '@/components/common/PageHeader';
import { MetricCard } from '@/components/common/MetricCard';
import { TierBadge } from '@/components/common/TierBadge';
import { LoadingSpinner } from '@/components/common/LoadingSpinner';
import { Card } from '@/components/ui/card';
import { SessionsChart, ReferralFunnelChart, PointsChart } from '@/components/charts/Charts';
import {
  Users,
  Calendar,
  TrendingUp,
  Award,
  Coins,
  Star,
  AlertCircle,
  Building2,
  FileText,
} from 'lucide-react';
import API from '@/api/client';
import { formatNumber, formatCurrency } from '@/utils/helpers';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';

const AdminDashboard = () => {
  const [loading, setLoading] = useState(true);
  const [dashboardData, setDashboardData] = useState(null);

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      const response = await API.get('/admin/dashboard');
      setDashboardData(response.data);
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
      toast.error('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return <LoadingSpinner size="lg" text="Loading dashboard..." />;
  }

  if (!dashboardData) {
    return (
      <div className="text-center py-12">
        <p className="text-muted-foreground">Failed to load dashboard data</p>
      </div>
    );
  }

  const { kpis, top_therapists, top_referrers } = dashboardData;

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 shadow-sm sticky top-0 z-50 px-4 md:px-6 py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <img
              src="https://customer-assets.emergentagent.com/job_e65b2a0c-b7a8-46e9-8a92-d5df4207b5ae/artifacts/8vn39jol_Physiomobile%20Logo.jpeg"
              alt="Physiomobile Logo"
              className="h-10"
            />
            <h1 className="font-heading text-2xl font-bold text-gray-800">
              Flywheel HQ
            </h1>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="px-4 md:px-6 lg:px-8 py-8">
        <PageHeader
          title="Flywheel Dashboard"
          subtitle="Monitor your key metrics and performance indicators"
        />

        {/* Quick Navigation */}
        <div className="flex gap-2 mb-6 overflow-x-auto pb-2">
          <Button onClick={() => window.location.href = '/admin/therapists'} variant="outline" size="sm">
            <Users className="w-4 h-4 mr-2" />
            Therapists
          </Button>
          <Button onClick={() => window.location.href = '/admin/referrals'} variant="outline" size="sm">
            <TrendingUp className="w-4 h-4 mr-2" />
            Referrals
          </Button>
          <Button onClick={() => window.location.href = '/admin/points'} variant="outline" size="sm">
            <Coins className="w-4 h-4 mr-2" />
            Points
          </Button>
          <Button onClick={() => window.location.href = '/admin/content'} variant="outline" size="sm">
            <FileText className="w-4 h-4 mr-2" />
            Content
          </Button>
          <Button onClick={() => window.location.href = '/admin/corporate'} variant="outline" size="sm">
            <Building2 className="w-4 h-4 mr-2" />
            Corporate
          </Button>
        </div>

        {/* KPI Metrics Grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-6 mb-8">
          <MetricCard
            title="Total Sessions"
            value={formatNumber(kpis.total_sessions)}
            subtitle={`${formatNumber(kpis.sessions_30d)} in last 30 days`}
            icon={Calendar}
            testId="metric-total-sessions"
          />

          <MetricCard
            title="Active Customers"
            value={formatNumber(kpis.active_customers)}
            subtitle="Monthly Active Users"
            icon={Users}
            testId="metric-active-customers"
          />

          <MetricCard
            title="Active Therapists"
            value={formatNumber(kpis.active_therapists)}
            icon={Award}
            testId="metric-active-therapists"
          />

          <MetricCard
            title="Referral Conversions"
            value={`${formatNumber(kpis.referral_conversions)} / ${formatNumber(kpis.total_referrals)}`}
            subtitle={`${Math.round((kpis.referral_conversions / kpis.total_referrals) * 100)}% conversion rate`}
            icon={TrendingUp}
            testId="metric-referral-conversions"
          />

          <MetricCard
            title="Points Issued"
            value={formatNumber(kpis.points_issued)}
            subtitle={`${formatNumber(kpis.points_redeemed)} redeemed`}
            icon={Coins}
            testId="metric-points-issued"
          />

          <MetricCard
            title="Avg Session Rating"
            value={kpis.avg_rating.toFixed(1)}
            subtitle="Out of 5.0"
            icon={Star}
            testId="metric-avg-rating"
          />

          <MetricCard
            title="Cancellation Rate"
            value={`${kpis.cancellation_rate}%`}
            icon={AlertCircle}
            testId="metric-cancellation-rate"
          />

          <MetricCard
            title="Completed Sessions"
            value={formatNumber(kpis.completed_sessions)}
            subtitle={`${Math.round((kpis.completed_sessions / kpis.total_sessions) * 100)}% completion`}
            icon={Calendar}
            testId="metric-completed-sessions"
          />
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Top Therapists */}
          <Card className="p-6" data-testid="top-therapists-card">
            <h2 className="font-heading text-2xl font-semibold mb-4 text-gray-800">
              Top Therapists
            </h2>
            <div className="space-y-4">
              {top_therapists.slice(0, 5).map((therapist, index) => (
                <div
                  key={therapist.id}
                  className="flex items-center justify-between p-4 rounded-lg bg-gray-50 border border-gray-200 hover:bg-gray-100 transition-colors"
                  data-testid={`top-therapist-${index}`}
                >
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center">
                      <span className="font-heading font-bold text-primary">
                        {index + 1}
                      </span>
                    </div>
                    <div>
                      <p className="font-subheading font-medium text-gray-800">
                        {therapist.user_id.slice(0, 8)}
                      </p>
                      <p className="text-sm text-muted-foreground">
                        {therapist.specialization} • {therapist.region}
                      </p>
                    </div>
                  </div>
                  <div className="text-right">
                    <TierBadge tier={therapist.tier} size="sm" />
                    <p className="text-sm text-muted-foreground mt-1">
                      {formatNumber(therapist.total_sessions)} sessions
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </Card>

          {/* Top Referrers */}
          <Card className="p-6" data-testid="top-referrers-card">
            <h2 className="font-heading text-2xl font-semibold mb-4 text-gray-800">
              Top Referrers
            </h2>
            <div className="space-y-4">
              {top_referrers.slice(0, 5).map((referrer, index) => (
                <div
                  key={referrer._id}
                  className="flex items-center justify-between p-4 rounded-lg bg-gray-50 border border-gray-200 hover:bg-gray-100 transition-colors"
                  data-testid={`top-referrer-${index}`}
                >
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center">
                      <span className="font-heading font-bold text-primary">
                        {index + 1}
                      </span>
                    </div>
                    <div>
                      <p className="font-subheading font-medium text-gray-800">
                        {referrer._id.slice(0, 8)}
                      </p>
                      <p className="text-sm text-muted-foreground">
                        {referrer.converted} / {referrer.total_referrals} converted
                      </p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="font-heading text-xl font-bold text-primary">
                      {referrer.total_referrals}
                    </p>
                    <p className="text-xs text-muted-foreground">referrals</p>
                  </div>
                </div>
              ))}
            </div>
          </Card>
        </div>

        {/* Quick Stats Summary */}
        <Card className="p-6 mt-6" data-testid="stats-summary-card">
          <h2 className="font-heading text-2xl font-semibold mb-4 text-gray-800">
            Quick Stats
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="p-4 rounded-lg bg-green-50 border border-green-200">
              <p className="text-sm font-medium text-green-800 mb-1">
                Sessions Growth Target
              </p>
              <p className="text-2xl font-bold text-green-900">
                {formatNumber(12000)} → {formatNumber(25664)}
              </p>
              <p className="text-xs text-green-700 mt-1">
                114% growth target for next year
              </p>
            </div>

            <div className="p-4 rounded-lg bg-blue-50 border border-blue-200">
              <p className="text-sm font-medium text-blue-800 mb-1">
                Points Economy
              </p>
              <p className="text-2xl font-bold text-blue-900">
                {Math.round((kpis.points_redeemed / kpis.points_issued) * 100)}%
              </p>
              <p className="text-xs text-blue-700 mt-1">
                Breakage rate (unused points)
              </p>
            </div>

            <div className="p-4 rounded-lg bg-purple-50 border border-purple-200">
              <p className="text-sm font-medium text-purple-800 mb-1">
                Quality Score
              </p>
              <p className="text-2xl font-bold text-purple-900">
                {kpis.avg_rating.toFixed(1)} / 5.0
              </p>
              <p className="text-xs text-purple-700 mt-1">
                Average session rating
              </p>
            </div>
          </div>
        </Card>
      </main>
    </div>
  );
};

export default AdminDashboard;
